import { type ReactNode, useEffect, useMemo, useState } from "react";

import { Check, Filter, FilterX, Package, X } from "lucide-react";

import { Button } from "@components/button/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/dropdown-menu/dropdown-menu";

import type { IOpcaoProdutoFiltroCobranca } from "@/hooks/cobranca-credito-cliente/buscar-cobranca-por-cliente";

import { cn } from "@/data/lib/tailwind-merge";
import type { IFiltrosCobrancaCreditoCliente } from "@/interface/extrato-credito-cliente";

type BarraFiltrosCobrancaCreditoProps = {
  filtros: IFiltrosCobrancaCreditoCliente;
  onAlterarFiltros: (patch: Partial<IFiltrosCobrancaCreditoCliente>) => void;
  onLimparFiltros: () => void;
  onAplicarMesAtual: () => void;
  opcoesProduto: IOpcaoProdutoFiltroCobranca[];
  carregandoProdutos: boolean;
};

function SegmentoPill({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex items-center px-2 py-1 text-xs text-neutral-700", className)}>
      {children}
    </span>
  );
}

function DivisorPill() {
  return <span className="h-4 w-px shrink-0 bg-neutral-200" aria-hidden />;
}

function formatarPeriodoResumo(dataInicio?: string, dataFim?: string): string {
  if (!dataInicio && !dataFim) {
    return "Todos";
  }
  try {
    const fmt = (iso: string) =>
      new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR", { dateStyle: "short" });
    if (dataInicio && dataFim) {
      return `${fmt(dataInicio)} — ${fmt(dataFim)}`;
    }
    if (dataInicio) {
      return `A partir de ${fmt(dataInicio)}`;
    }
    return `Até ${fmt(dataFim!)}`;
  } catch {
    return "Período";
  }
}

export function BarraFiltrosCobrancaCredito({
  filtros,
  onAlterarFiltros,
  onLimparFiltros,
  onAplicarMesAtual,
  opcoesProduto,
  carregandoProdutos,
}: BarraFiltrosCobrancaCreditoProps) {
  const [menuPeriodoAberto, setMenuPeriodoAberto] = useState(false);
  const [rascunhoDataInicio, setRascunhoDataInicio] = useState("");
  const [rascunhoDataFim, setRascunhoDataFim] = useState("");

  useEffect(() => {
    if (menuPeriodoAberto) {
      setRascunhoDataInicio(filtros.dataInicio ?? "");
      setRascunhoDataFim(filtros.dataFim ?? "");
    }
  }, [menuPeriodoAberto, filtros.dataInicio, filtros.dataFim]);

  const possuiAlgumFiltro = useMemo(
    () => filtros.produtoId != null || Boolean(filtros.dataInicio) || Boolean(filtros.dataFim),
    [filtros]
  );

  const nomeProdutoSelecionado = useMemo(() => {
    if (filtros.produtoId == null) {
      return "Todos";
    }
    return (
      opcoesProduto.find((p) => p.produtoId === filtros.produtoId)?.nome ??
      `ID ${filtros.produtoId}`
    );
  }, [filtros.produtoId, opcoesProduto]);

  const resumoPeriodo = formatarPeriodoResumo(filtros.dataInicio, filtros.dataFim);

  return (
    <div className="flex w-full max-w-full flex-wrap items-center justify-end gap-2">
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-600 shadow-sm"
        title="Filtros da cobrança"
        aria-hidden
      >
        <Filter className="h-4 w-4" />
      </div>

      <div className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 text-sm shadow-sm">
        <SegmentoPill className="gap-1 pl-3">
          <Package className="h-3.5 w-3.5 text-neutral-500" aria-hidden />
          <span className="font-medium text-neutral-800">Produto</span>
        </SegmentoPill>
        <DivisorPill />
        <SegmentoPill className="text-muted-foreground">é</SegmentoPill>
        <DivisorPill />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={carregandoProdutos}
              className="inline-flex max-w-[200px] items-center gap-1 rounded-r-full px-2 py-1 text-xs font-medium text-neutral-900 hover:bg-neutral-100 disabled:opacity-50"
            >
              <span className="truncate">{carregandoProdutos ? "…" : nomeProdutoSelecionado}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              onSelect={() => onAlterarFiltros({ produtoId: undefined })}
              className="flex justify-between gap-2"
            >
              <span>Todos</span>
              {filtros.produtoId == null ? <Check className="h-4 w-4 shrink-0" /> : null}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {opcoesProduto.map((opcao) => (
              <DropdownMenuItem
                key={opcao.produtoId}
                onSelect={() => onAlterarFiltros({ produtoId: opcao.produtoId })}
                className="flex justify-between gap-2"
              >
                <span className="truncate">{opcao.nome}</span>
                {filtros.produtoId === opcao.produtoId ? (
                  <Check className="h-4 w-4 shrink-0" />
                ) : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {filtros.produtoId != null ? (
          <button
            type="button"
            className="mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-200 hover:text-neutral-900"
            aria-label="Remover filtro de produto"
            onClick={() => onAlterarFiltros({ produtoId: undefined })}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      <div className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 text-sm shadow-sm">
        <SegmentoPill className="pl-3 font-medium text-neutral-800">Período</SegmentoPill>
        <DivisorPill />
        <SegmentoPill className="text-muted-foreground">é</SegmentoPill>
        <DivisorPill />
        <DropdownMenu open={menuPeriodoAberto} onOpenChange={setMenuPeriodoAberto}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex max-w-[220px] items-center gap-1 rounded-r-full px-2 py-1 text-xs font-medium text-neutral-900 hover:bg-neutral-100"
            >
              <span className="truncate">{resumoPeriodo}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-64 p-3"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  onAplicarMesAtual();
                  setMenuPeriodoAberto(false);
                }}
              >
                Mês atual
              </Button>
              <div className="space-y-1">
                <label
                  className="text-xs font-medium text-neutral-600"
                  htmlFor="cobranca-filtro-data-inicio"
                >
                  Data inicial
                </label>
                <input
                  id="cobranca-filtro-data-inicio"
                  type="date"
                  value={rascunhoDataInicio}
                  onChange={(e) => setRascunhoDataInicio(e.target.value)}
                  className="w-full rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-sm"
                />
              </div>
              <div className="space-y-1">
                <label
                  className="text-xs font-medium text-neutral-600"
                  htmlFor="cobranca-filtro-data-fim"
                >
                  Data final
                </label>
                <input
                  id="cobranca-filtro-data-fim"
                  type="date"
                  value={rascunhoDataFim}
                  onChange={(e) => setRascunhoDataFim(e.target.value)}
                  className="w-full rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setRascunhoDataInicio("");
                    setRascunhoDataFim("");
                    onAlterarFiltros({ dataInicio: undefined, dataFim: undefined });
                    setMenuPeriodoAberto(false);
                  }}
                >
                  Limpar datas
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    onAlterarFiltros({
                      dataInicio: rascunhoDataInicio || undefined,
                      dataFim: rascunhoDataFim || undefined,
                    });
                    setMenuPeriodoAberto(false);
                  }}
                >
                  Aplicar
                </Button>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        {(filtros.dataInicio != null && filtros.dataInicio !== "") ||
        (filtros.dataFim != null && filtros.dataFim !== "") ? (
          <button
            type="button"
            className="mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-200 hover:text-neutral-900"
            aria-label="Remover filtro de período"
            onClick={() => onAlterarFiltros({ dataInicio: undefined, dataFim: undefined })}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="shrink-0 gap-1.5 rounded-full border-neutral-200"
        disabled={!possuiAlgumFiltro}
        onClick={onLimparFiltros}
      >
        <FilterX className="h-4 w-4" />
        Limpar
      </Button>
    </div>
  );
}
